import { Flex, Heading, Text, useToast } from '@chakra-ui/react'
import { AxiosError } from 'axios'
import { DetailedUser, EditUser, UserProperties } from 'BrokerTsLib/src/dtos'
import {
  BadRequestError,
  ModelState,
} from 'brokertslib/src/dtos/Shared/ModelState'
import {
  useCreateUser,
  useGetAvailableInsurers,
  useGetOrganizationUnitById,
} from 'BrokerTsLib/src/hooks/useOrganizationUnit'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Container } from '@/components/Container'
import { Section } from '@/components/Section'
import { PageContent } from '@/containers/PageContent'
import { OrganizationLink } from '@/containers/shared/OrganizationLink'
import { UserEditForm } from '@/containers/users/UserEditForm'

export const OrganizationCreateUserPage = () => {
  const { organizationUnitId: organizationUnitIdParam } = useParams()
  const navigate = useNavigate()
  const toast = useToast()
  const organizationUnitId = Number(organizationUnitIdParam)

  const { data: organizationData, isFetched: isOrganizationFetched } =
    useGetOrganizationUnitById(organizationUnitId)
  const organization = organizationData?.data

  const { data: insurersData, isFetched: isInsurersFetched } =
    useGetAvailableInsurers(organizationUnitId)

  const [user, setUser] = useState<DetailedUser | null>(null)
  const [properties] = useState({} as UserProperties)

  useEffect(() => {
    if (!isOrganizationFetched || !isInsurersFetched) return

    const insurers = Array.from(
      new Set(
        insurersData?.data.map((x) => ({
          insurerId: x.insurerId,
          insurer: x.insurer,
        })),
      ),
    )
    // Speciální majetkové osobní číslo pro Uniqa
    const insurersProperty = insurers?.filter((x) => x.insurerId === 1)
    setUser({
      insuringAllowed: true,
      valid: true,
      organizationUnitId,
      personalNumbers: insurers?.map((i) => ({
        insurerId: i.insurerId,
        insurerName: i.insurer,
        acquisitionNumber: '',
      })),
      personalNumbersProperty: insurersProperty?.map((i) => ({
        insurerId: i.insurerId,
        insurerName: i.insurer,
        acquisitionNumber: '',
      })),
      properties: {
        vehicleInsuranceAllowed: true,
        propertyInsuranceAllowed: true,
        travelInsuranceAllowed: true,
      },
    } as EditUser)
  }, [isOrganizationFetched, isInsurersFetched])

  const { mutateAsync: create } = useCreateUser()

  const onSubmit = async (values: EditUser) => {
    const result = await create(values)
      .then(() => {
        navigate('/organization/' + organizationUnitId)
      })
      .catch((e: AxiosError) => {
        if (e.response?.status === 400)
          return new BadRequestError(e.response?.data as ModelState<EditUser>)

        toast({
          title: 'Chyba při vytváření uživatele',
          description: `${e.response?.status}: ${e.response?.statusText}`,
          status: 'error',
        })

        return e.cause ?? new Error('Unknown error')
      })
    return result
  }

  return (
    <Section>
      <Container>
        <PageContent>
          <Heading>Vytvořit nového uživatele</Heading>

          <Flex display={'flex'}>
            <Text>Organizace:&nbsp;</Text>
            <OrganizationLink name={organization?.name} id={organization?.id} />
          </Flex>

          {user && organization && (
            <UserEditForm
              user={user}
              properties={properties}
              onSubmit={onSubmit}
              isCreate={true}
              organization={organization}
            />
          )}
        </PageContent>
      </Container>
    </Section>
  )
}
